import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRIES, type CountryData } from "@/lib/countries";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";

/** Country names in the reader's language, sorted for that language. */
export function localizedCountries(lang: string): Array<CountryData & { label: string }> {
  const locale = lang === "en" ? "en" : "fr";
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    names = null;
  }
  return COUNTRIES.map((country) => ({ ...country, label: names?.of(country.iso) ?? country.name })).sort((a, b) =>
    a.label.localeCompare(b.label, locale),
  );
}

/**
 * A searchable country list. The value is the English name of the shared list
 * (what sign-up stores); the reader sees and searches names in their language,
 * the English name or the dialling code.
 */
export function CountrySelect({
  id,
  value,
  onChange,
  lang,
  placeholder,
  searchPlaceholder,
  emptyLabel,
}: {
  id?: string;
  value: string;
  onChange: (country: CountryData) => void;
  lang: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const countries = useMemo(() => localizedCountries(lang), [lang]);
  const selected = countries.find((country) => country.name === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>{selected ? selected.label : placeholder}</span>
          <Icon name="chevronDown" className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup className="max-h-[280px] overflow-y-auto">
              {countries.map((country) => (
                <CommandItem
                  key={country.iso}
                  value={`${country.label} ${country.name} ${country.dial}`}
                  onSelect={() => {
                    onChange(country);
                    setOpen(false);
                  }}
                  className="cursor-pointer gap-2"
                >
                  <span className="truncate">{country.label}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{country.dial}</span>
                  {selected?.iso === country.iso ? <Icon name="check" className="size-4 text-primary" aria-hidden /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
